/**
 * WasteStreamsEditor Component
 * Manages waste streams in the plan editor with inline editing
 */

import { useState } from 'react';
import type { WasteStream, ComplianceCalculation } from '../../types';
import { WasteStreamForm } from './WasteStreamForm';
import { formatWasteStreamType, formatDiversionMethod, formatWeight } from '../../utils/formatting';

interface WasteStreamsEditorProps {
  wasteStreams: WasteStream[];
  onChange: (wasteStreams: WasteStream[]) => void;
  compliance: ComplianceCalculation;
  readonly?: boolean;
}

export function WasteStreamsEditor({ wasteStreams, onChange, compliance, readonly = false }: WasteStreamsEditorProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAddWasteStream = (wasteStream: WasteStream) => {
    if (editingIndex !== null) {
      // Update existing
      const updated = [...wasteStreams];
      updated[editingIndex] = wasteStream;
      onChange(updated);
      setEditingIndex(null);
    } else {
      // Add new
      onChange([...wasteStreams, wasteStream]);
    }
    setShowForm(false);
  };

  const handleEditWasteStream = (index: number) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDeleteWasteStream = (index: number) => {
    onChange(wasteStreams.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      {wasteStreams.length > 0 && (
        <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-neutral-600 mb-1">Total Materials</div>
              <div className="text-xl font-semibold text-neutral-900">
                {wasteStreams.length}
              </div>
            </div>
            <div>
              <div className="text-sm text-neutral-600 mb-1">Total Tonnage</div>
              <div className="text-xl font-semibold text-neutral-900">
                {formatWeight(compliance.total_waste_generated)}
              </div>
            </div>
            <div>
              <div className="text-sm text-neutral-600 mb-1">Diversion Rate</div>
              <div
                className={`text-xl font-semibold ${
                  compliance.meets_calgreen_requirement ? 'text-accent-600' : 'text-red-600'
                }`}
              >
                {compliance.diversion_rate.toFixed(1)}%
              </div>
              <div className="text-xs text-neutral-600 mt-1">
                {compliance.meets_calgreen_requirement ? 'Meets' : 'Does Not Meet'} Requirements
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Waste Streams List */}
      {wasteStreams.length > 0 && !showForm && (
        <div className="space-y-3">
          {wasteStreams.map((stream, index) => (
            <div
              key={index}
              className="card border border-neutral-200 hover:border-neutral-300 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-neutral-900">
                      {formatWasteStreamType(stream.material_type)}
                    </h4>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        stream.diversion_method === 'landfill'
                          ? 'bg-neutral-100 text-neutral-700'
                          : 'bg-accent-100 text-accent-700'
                      }`}
                    >
                      {formatDiversionMethod(stream.diversion_method)}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-600 space-y-1">
                    <div>Quantity: {formatWeight(stream.estimated_quantity)}</div>
                    {stream.facility_name && (
                      <div>
                        <span className="font-medium">Facility:</span> {stream.facility_name}
                      </div>
                    )}
                    {stream.facility_address && (
                      <div className="text-neutral-500">{stream.facility_address}</div>
                    )}
                  </div>
                </div>

                {!readonly && (
                  <div className="flex gap-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEditWasteStream(index)}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      aria-label={`Edit ${formatWasteStreamType(stream.material_type)} waste stream`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteWasteStream(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label={`Delete ${formatWasteStreamType(stream.material_type)} waste stream`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-neutral-900">
                  {editingIndex !== null ? 'Edit Waste Stream' : 'Add Waste Stream'}
                </h3>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="p-2 text-neutral-500 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <WasteStreamForm
                initialData={editingIndex !== null ? wasteStreams[editingIndex] : undefined}
                onSubmit={handleAddWasteStream}
                onCancel={handleCancel}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Waste Stream Button */}
      {!showForm && !readonly && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full btn-secondary border-2 border-dashed border-neutral-300 hover:border-primary-400 hover:bg-primary-50 transition-all"
        >
          <svg
            className="w-5 h-5 inline-block mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Material
        </button>
      )}

      {/* Empty State */}
      {wasteStreams.length === 0 && !showForm && (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-200">
          <svg
            className="w-12 h-12 mx-auto text-neutral-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-neutral-600 mb-4">No waste streams added yet</p>
          {!readonly && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="btn-primary"
            >
              Add Your First Material
            </button>
          )}
        </div>
      )}
    </div>
  );
}
